// MACHINERY V2 (Stroje) — the state-contract card (M2.1).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §4.3 (LOCKED).
// Reference impl: design_handoff_machinery_v3/machinery-states.jsx (StateCard).
//
// This is the composition that proves the contract: identity block → status
// row (badge + since + conn dot) → identity strip → exactly ONE footer
// (alarm strip | program line | no-telemetry notice). It is the reference
// M2.2's FleetCard composes against — the fleet card adds the photo,
// capability badges (M2.9), kebab actions and the Midas row on top of these
// same atoms; it must NOT re-implement the status rendering.
//
// Deliberately NOT included here (owned by other sub-phases, so this file
// stays the status contract and nothing else): MachineImage (M2.2),
// CapabilityBadges + Midas readiness row (M2.9), kebab menu (M2.2).
//
// EVERY paint decision comes from deriveCardPresentation(telemetry). This
// component reads its flags and never re-tests raw telemetry fields.
// Footer precedence (exactly one, never two), decided there:
//   1. alarm strip      — an active fault outranks the STATUS FIELD, including
//                         one that still claims "running". It does NOT outrank
//                         a connectivity verdict: an `offline` machine keeps
//                         the offline STATE and shows its alarm as LAST-KNOWN
//                         (presentation.alarmLastKnown, not staleLook)
//   2. program line     — only while running (a stale running machine keeps
//                         its last-known line, artboard state 07)
//   3. no-telemetry     — only when there is no connection row at all
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import AlarmStrip from "./AlarmStrip";
import ConnDot from "./ConnDot";
import ProgramLine from "./ProgramLine";
import StatusBadge, { AwaitingBadge } from "./StatusBadge";
import StatusIcon from "./StatusIcon";
import {
  MachineryV2CardMachine,
  MachineryV2Status,
  MachineryV2Telemetry,
  deriveCardPresentation,
} from "./cardStates";

/** Footer for machines with no MachineConnection row. Dashed, quiet, faint —
 *  this is the absence of a thing, not a warning. */
export const NoTelemetryFooter: React.FC = () => {
  const { translate } = useTranslation("myMachinery");
  return (
    <div
      style={{
        padding: "8px 12px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        letterSpacing: "0.04em",
        color: "var(--faint)",
        borderTop: "1px dashed var(--border)",
        background: "transparent",
      }}
    >
      <span style={{ opacity: 0.7, display: "inline-flex" }}>
        <StatusIcon kind="cloudoff" size={11} />
      </span>
      <span style={{ textTransform: "uppercase" }}>
        {translate("myMachinery:v2.noTelemetry")}
      </span>
      <span
        style={{
          opacity: 0.6,
          letterSpacing: 0,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        · {translate("myMachinery:v2.noTelemetryHint")}
      </span>
    </div>
  );
};

const PinGlyph: React.FC = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden>
    <path
      d="M5.5 1.2c-1.7 0-3 1.3-3 3 0 2 3 5.6 3 5.6s3-3.6 3-5.6c0-1.7-1.3-3-3-3z"
      stroke="currentColor"
    />
    <circle cx="5.5" cy="4.3" r="1.1" stroke="currentColor" />
  </svg>
);

/** "od 06:49 · 2h 14m" for running/idle, "od 23:18" for alarm, null when the
 *  adapter did not supply a start time. Never fabricates an elapsed value.
 *  `status` is the DERIVED badge status, not the raw telemetry field, and
 *  `prefix` is the whole prefix — the caller REPLACES it for last-known data
 *  rather than stacking a second word in front of it. */
const sinceClause = (
  t: MachineryV2Telemetry,
  status: MachineryV2Status | null,
  prefix: string
): string | null => {
  if (!t.since || !status) return null;
  if (status === "running" || status === "idle") {
    return t.elapsed
      ? prefix + " " + t.since + " · " + t.elapsed
      : prefix + " " + t.since;
  }
  if (status === "alarm") return prefix + " " + t.since;
  return null;
};

export interface StateCardProps {
  machine: MachineryV2CardMachine;
}

const StateCard: React.FC<StateCardProps> = ({ machine }) => {
  const { translate } = useTranslation("myMachinery");
  const t = machine.telemetry;

  // ONE derivation, consumed as flags. This component must never re-test raw
  // telemetry (alarms.length, conn, status) to decide what to paint — that
  // split brain is what produced a RUNNING badge above an alarm strip.
  const p = deriveCardPresentation(t);
  const alarm = p.showAlarmStrip ? t.alarms[0] : null;
  // Prefix REPLACEMENT, not accumulation (AlarmStrip does the same): a stale
  // card reads "(naposledy) 06:03 · 3h 00m", never "naposledy od 06:03".
  const since = sinceClause(
    t,
    p.badgeStatus,
    p.staleLook
      ? "(" + translate("myMachinery:v2.lastKnown") + ")"
      : translate("myMachinery:v2.sincePrefix")
  );

  return (
    <article
      style={{
        position: "relative", background: "var(--card)", borderRadius: 10,
        border: "1px solid var(--border)", overflow: "hidden",
        boxShadow: "var(--shadow)", display: "flex", flexDirection: "column",
        width: "100%", minWidth: 0,
      }}
    >
      {/* Identity */}
      <div style={{ padding: "12px 12px 10px", minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600, fontSize: 12.5, color: "var(--ink)", lineHeight: 1.2,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          {machine.brand} {machine.model}
        </div>
        <div
          style={{
            fontSize: 11, color: "var(--muted)", marginTop: 1,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          {machine.typeLabel}
        </div>
        {machine.locationLabel && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 10.5,
              color: "var(--ink-2)",
              marginTop: 4,
              minWidth: 0,
            }}
          >
            <span style={{ color: "var(--mod-accent)", display: "inline-flex" }}>
              <PinGlyph />
            </span>
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {machine.locationLabel}
              {machine.subLocationLabel ? " › " + machine.subLocationLabel : ""}
            </span>
          </div>
        )}
      </div>

      {/* Status row — omitted ENTIRELY when unconnected (§4.3). */}
      {p.showStatusRow && (
        <div
          style={{
            margin: "0 12px 10px",
            padding: "8px 0 0",
            borderTop: "1px solid var(--border-soft)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {p.showAwaitingBadge ? (
            <AwaitingBadge />
          ) : (
            <StatusBadge status={p.badgeStatus} stale={p.staleLook} variant="chip" />
          )}
          {since && (
            <span
              style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--faint)" }}
            >
              {since}
            </span>
          )}
          <span style={{ flex: 1 }} />
          {/* The conn dot is the SEPARATE connectivity track (§4.3), not a
              second read of the card state — it takes `conn` by design and
              renders nothing for unconnected/awaiting. */}
          <ConnDot state={t.conn} lastHeartbeat={t.heartbeat} />
        </div>
      )}

      {/* Identity strip */}
      <div
        style={{
          margin: "0 12px",
          padding: p.showStatusRow ? "0 0 10px" : "6px 0 10px",
          borderTop: p.showStatusRow ? "0" : "1px solid var(--border-soft)",
          display: "flex",
          gap: 14,
          fontSize: 11,
          color: "var(--ink-2)",
          minWidth: 0,
        }}
      >
        <span style={{ whiteSpace: "nowrap" }}>
          <span
            style={{
              color: "var(--faint)",
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              letterSpacing: "0.06em",
            }}
          >
            SN{" "}
          </span>
          <span style={{ fontFamily: "var(--font-mono)" }}>
            {machine.serialNumber || "—"}
          </span>
        </span>
        <span style={{ color: "var(--border)" }}>·</span>
        <span
          style={{
            color: "var(--muted)",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {machine.controlSystem || "—"}
        </span>
      </div>

      {/* Exactly ONE footer. */}
      {alarm ? (
        <AlarmStrip
          code={alarm.code}
          message={alarm.message}
          since={alarm.since}
          lastKnown={p.alarmLastKnown}
        />
      ) : p.showProgramLine && t.program ? (
        <ProgramLine
          program={t.program}
          partCount={t.partCount}
          spindleRpm={t.spindleRpm}
          feedPercent={t.feedPercent}
        />
      ) : p.showNoTelemetry ? (
        <NoTelemetryFooter />
      ) : null}
    </article>
  );
};

export default StateCard;
